/**
 * VaultShareDrawer — the Sharing Manager for one Vault node (folder or file).
 *
 * TWO independent systems shown as two tabs, exactly as the backend keeps them:
 *   - "People" → vault_shares  (grant a user/tenant view/edit/delete/reshare)
 *   - "Agents" → vault_agent_links ("used by": which chatbots/skills/tenants
 *                reach this node — list + unlink)
 * A person share grants an agent nothing, and vice-versa.
 */

import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { endPlacement } from '@/utils/rtl';
import {
  Drawer, Tabs, Button, Checkbox, Input, Space, Spin, Popconfirm, message, Modal, Tag,
  Tooltip,
} from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@modules/auth';
import { BotAvatar } from '@/components/common/BotAvatar';
import botService from '@modules/chatbots/services/botService';
import { SkillsApi } from '@modules/skills/services/skillsApi';
import type { Skill } from '@modules/skills/types';
import type { Bot } from '@/types/models';
import type { ShareableUser, ShareableTenant } from '@/types/admin';
import { vaultService } from '../vaultService';
// This stylesheet existed but was never imported — the drawer's own rules
// (grant footer, inherited chips, blocked-share panel) silently never applied
// and the drawer lived off VaultPage.css globals. Found 2026-08-20.
import './VaultShareDrawer.css';
import type {
  BulkItem, BulkOutcome, VaultAgentLink, VaultAccess, VaultAudience, VaultAudienceEntry,
} from '../types';

/**
 * A line that its column may be too narrow to hold, which says the whole thing
 * on hover — and only then.
 *
 * The rows carry sentences: "From the folder Cross Organizations folder",
 * "Managed on Cross Organizations folder". Ellipsised, they name a folder you
 * cannot read, which is worse than naming none (field report 2026-08-29).
 *
 * The check happens on the element itself, at hover time: scrollWidth beats
 * clientWidth only when the browser actually had to clip it. So a line that
 * fits gets no tooltip — a bubble repeating what you can already read is
 * noise — and nothing is measured until somebody points at a row.
 */
function Clipped({ as: Tag = 'div', className, title, children }: {
  as?: 'div' | 'span';
  className?: string;
  title: string;
  children: ReactNode;
}) {
  const [clipped, setClipped] = useState(false);
  return (
    <Tooltip title={clipped ? title : ''} mouseEnterDelay={0.25} placement="top">
      <Tag
        className={className}
        onMouseEnter={(e: MouseEvent<HTMLElement>) => {
          const el = e.currentTarget;
          setClipped(el.scrollWidth > el.clientWidth + 1);
        }}
      >
        {children}
      </Tag>
    </Tooltip>
  );
}

/** One key per person or organization in the audience, whatever it came from. */
const rowKey = (r: { kind: string; user_id?: string; tenant_id?: string }) =>
  `${r.kind}:${r.user_id || r.tenant_id}`;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const BotGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <rect x="5" y="8" width="14" height="11" rx="3" />
    <path d="M12 8V5M9 13h.01M15 13h.01" strokeLinecap="round" />
  </svg>
);

const EditGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const DeleteGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

const ReshareGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="12" r="2.6" /><circle cx="17.5" cy="5.5" r="2.6" /><circle cx="17.5" cy="18.5" r="2.6" />
    <path d="M8.4 10.7 15 6.9M8.4 13.3l6.6 3.8" />
  </svg>
);

const SkillGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 14.2 8.6 20 9.3l-4.3 4 1.2 5.7L12 16.3 7.1 19l1.2-5.7L4 9.3l5.8-.7z" />
  </svg>
);

interface Props {
  node: { folder_id?: string; file_id?: string; name: string };
  access: VaultAccess;
  onClose: () => void;
  /**
   * Share several selected items in one pass. The drawer is then the People
   * grant alone: the same picker, rights and organization placement, applied
   * to every item through the bulk route — which answers per item. There is
   * no single audience to show, and agent links stay a one-node act. `node`
   * still names ONE of the items; it is what the share-target list is asked
   * about (the targets depend on the person, not on which item).
   */
  bulk?: {
    items: BulkItem[];
    names: string[];
    onDone: (outcome: BulkOutcome) => void;
  };
}

export function VaultShareDrawer({ node, access, onClose, bulk }: Props) {
  const { t, direction } = useLanguage();
  const { isAdmin: isAdminRole } = useAuth();
  // Agent links are made one node at a time; a selection only grants people.
  const isAdmin = isAdminRole && !bulk;
  const nodeRef = node.folder_id ? { folder_id: node.folder_id } : { file_id: node.file_id! };

  const [loading, setLoading] = useState(true);
  // Which tab is open. Controlled, so a refresh after granting/revoking cannot
  // bounce the user back to the first tab.
  /* Opens on AGENTS — what reaches this node is the question people come
     here with; permissions are the deliberate second step (operator
     decision 2026-08-26). Someone who cannot manage links has no Agents tab
     at all, so they still land on Permissions. */
  const [activeTab, setActiveTab] = useState(isAdmin ? 'agents' : 'people');
  const [audience, setAudience] = useState<VaultAudience>({
    organizations: [], people: [], managers: [],
  });
  const [usedBy, setUsedBy] = useState<VaultAgentLink[]>([]);
  // Whose rights are being rewritten right now, and to what. One at a time:
  // an open editor on every row would be a form, not a list.
  const [editing, setEditing] = useState<string | null>(null);
  const [editRights, setEditRights] = useState(
    { can_edit: false, can_delete: false, can_reshare: false },
  );
  const [savingRights, setSavingRights] = useState(false);
  const [targets, setTargets] = useState<{ users: ShareableUser[]; tenants: ShareableTenant[] }>({
    users: [],
    tenants: [],
  });

  // A SET, not one key: granting the same folder to several organizations (or
  // several people) is one decision made once, and making the person repeat
  // pick → Grant → pick → Grant per tenant is the same decision typed N times.
  const [granteeKeys, setGranteeKeys] = useState<string[]>([]);
  const toggleGrantee = (k: string | undefined) => {
    if (!k) return;
    setGranteeKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };
  const [granteeKind, setGranteeKind] = useState<'user' | 'tenant'>('user');
  // Organization shares come in two strengths; quiet is the default so that
  // picking an organization behaves like picking people unless the sharer
  // explicitly asks for the shelf.
  const [toShelf, setToShelf] = useState(false);
  const [granteeQuery, setGranteeQuery] = useState('');
  const [rights, setRights] = useState({ can_edit: false, can_delete: false, can_reshare: false });
  const [submitting, setSubmitting] = useState(false);

  // Agent picker (the Agents tab grants access too, not just revokes it).
  // A skill is a subject in its own right: an agent that loads the skill reads
  // the skill's files, so a folder can be attached to the skill once instead of
  // to every agent that uses it.
  const [bots, setBots] = useState<Bot[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [subjectKey, setSubjectKey] = useState<string | undefined>();
  const [subjectQuery, setSubjectQuery] = useState('');
  const [linking, setLinking] = useState(false);
  // Step 2 of the Agents flow: an agent is chosen, now decide WHERE the folder
  // goes — the agent's own knowledge, or one of the skills it loads.
  const [pickedAgent, setPickedAgent] = useState<Bot | null>(null);
  const [agentSkills, setAgentSkills] = useState<
    { skill_id: string; skill_name: string; can_edit?: boolean }[]
  >([]);
  const [agentSkillsLoading, setAgentSkillsLoading] = useState(false);
  const [destination, setDestination] = useState<string>('agent');
  const [asContext, setAsContext] = useState(false);

  // A selection reaches this drawer only when every item allows sharing —
  // the bar asked the server first.
  const canManage = !!bulk || access.is_manager || access.can_reshare;
  /* Organizations only go on a SHELF as folders; files are always quiet. */
  const anyFolder = bulk ? bulk.items.some((i) => i.kind === 'folder') : !!node.folder_id;
  /* Who may attach this folder to an agent, a skill or the organization's
     context. That is a ROLE question — the /agent-links routes are
     manager-only — not a question about this particular folder. Reading it
     off the folder hid all three tabs from a group admin looking at their OWN
     "Linked files" home, because a personal folder deliberately grants nobody
     manager rights, themselves included. They were left with People and no
     way to give a folder to the very agents it was created from. */
  const canManageAgentLinks = isAdmin;

  /**
   * Reload shares + agent links.
   *
   * `silent` refreshes WITHOUT flipping the loading flag: the drawer renders a
   * spinner in place of the whole body while loading, so a non-silent reload
   * after a grant would blank the sections and lose the user's scroll position
   * next to the row they just created.
   */
  const load = useCallback(async (silent = false) => {
    if (bulk) { setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const [aud, ub] = await Promise.all([
        // Anyone who can see the item can see who else can open it.
        vaultService.getAudience(nodeRef)
          .catch(() => ({ organizations: [], people: [], managers: [] })),
        canManageAgentLinks ? vaultService.usedBy(nodeRef) : Promise.resolve([]),
      ]);
      setAudience(aud);
      setUsedBy(ub);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.share.loadFailed'));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.folder_id, node.file_id]);

  useEffect(() => {
    load();
    // Only fetch the target list for someone who may actually grant access, and
    // say so when it fails — an empty picker with no explanation is how a person
    // ends up believing the feature is missing.
    if (canManage) {
      vaultService
        .getShareTargets(nodeRef)
        .then(setTargets)
        .catch((e) => message.error(e?.response?.data?.error || t('vault.share.targetsFailed')));
    }
    if (canManageAgentLinks) {
      botService.getBots().then(setBots).catch(() => undefined);
      SkillsApi.listAvailableSkills().then((r) => setSkills(r.skills)).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, canManage]);

  const handleAddShare = async () => {
    if (!granteeKeys.length) return;
    setSubmitting(true);
    if (bulk) {
      try {
        const outcome = await vaultService.bulkShare(
          bulk.items,
          granteeKeys.map((key) => {
            const [kind, id] = key.split(':');
            return kind === 'tenant' ? { tenant_id: id } : { user_id: id };
          }),
          rights,
          toShelf,
        );
        bulk.onDone(outcome);
      } catch (e: any) {
        message.error(e?.response?.status === 429
          ? t('vault.bulk.rateLimited')
          : e?.response?.data?.error || t('vault.share.grantFailed'));
        setSubmitting(false);
      }
      return;
    }
    // Sequential, not Promise.all: each grant re-reads the node's shares
    // server-side, and firing them together made two grants race on the same
    // row. A handful of grantees is not worth a race for.
    const failed: string[] = [];
    let lastError: any = null;
    try {
      for (const key of granteeKeys) {
        const [kind, id] = key.split(':');
        try {
          await vaultService.createShare({
            ...nodeRef,
            ...(kind === 'user' ? { shared_with_user_id: id } : { shared_with_tenant_id: id }),
            ...rights,
            // Explicit for tenant grantees: the backend defaults to the shelf
            // (the pre-quiet-share behaviour), and this drawer defaults to quiet.
            ...(kind === 'tenant' ? { to_shelf: toShelf } : {}),
          });
        } catch (err: any) {
          // One refusal must not discard the grants that DID land, nor hide
          // itself — name what failed and keep the rest.
          failed.push(nameForKey(key));
          lastError = err;
        }
      }
      if (failed.length && failed.length === granteeKeys.length) {
        throw lastError;
      }
      if (failed.length) {
        message.warning(t('vault.share.grantedPartial', {
          count: granteeKeys.length - failed.length,
          failed: failed.join(', '),
        }));
      } else {
        message.success(granteeKeys.length > 1
          ? t('vault.share.grantedMany', { count: granteeKeys.length })
          : t('vault.share.granted'));
      }
      setGranteeKeys([]);
      setGranteeQuery('');
      setRights({ can_edit: false, can_delete: false, can_reshare: false });
      load(true);
    } catch (e: any) {
      const data = e?.response?.data;
      // "This folder holds things you cannot pass on" is a paragraph with a
      // list and two suggestions in it. A toast that disappears in three
      // seconds is the wrong shape for that, so it gets a panel that waits to
      // be read — and the names come back as data, so it reads in Hebrew too.
      if (data?.code === 'unshareable_contents') {
        Modal.error({
          title: t('vault.share.blockedTitle'),
          content: (
            <div className="vault-share-blocked">
              <p>{t('vault.share.blockedBody')}</p>
              <ul>
                {(data.items || []).slice(0, 8).map((name: string) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              {(data.items || []).length > 8 && (
                <p className="more">
                  {t('vault.share.blockedMore', { n: data.items.length - 8 })}
                </p>
              )}
              <p>{t('vault.share.blockedHint')}</p>
            </div>
          ),
          okText: t('vault.share.blockedOk'),
        });
      } else {
        message.error(data?.error || t('vault.share.grantFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    try {
      await vaultService.revokeShare(shareId);
      message.success(t('vault.share.revoked'));
      load(true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.share.revokeFailed'));
    }
  };

  /** Load the skills an agent carries, so step 2 can offer them. */
  const choosePickedAgent = async (bot: Bot) => {
    setPickedAgent(bot);
    setDestination('agent');
    setAsContext(false);
    setAgentSkills([]);
    setAgentSkillsLoading(true);
    try {
      const rows = await SkillsApi.getChatbotSkills(bot.chatbot_id, true);
      setAgentSkills(
        rows
          .map((r: any) => ({
            skill_id: r.skill_id || r.skill?.skill_id,
            skill_name: r.skill_name || r.skill?.skill_name,
            can_edit: r.can_edit,
          }))
          // Only the skills this person may actually change. Putting a folder
          // into a skill IS editing that skill, so offering one they do not own
          // — a skill shared in from another organization, or any skill at all
          // to someone who is not an admin — offers an act the server will
          // refuse. The agent's own knowledge is always still there.
          .filter((r: any) => r.skill_id && r.skill_name && r.can_edit !== false
            && !linkedSkillIds.has(r.skill_id)),
      );
    } catch {
      setAgentSkills([]);   // no skills offered rather than a broken step 2
    } finally {
      setAgentSkillsLoading(false);
    }
  };

  const handleAddAgentLink = async () => {
    // Three ways in: the Skills tab picks a skill, the Organization tab picks a
    // tenant context, and the Agents tab picks an agent and then a destination
    // inside it. All of them write the same kind of row.
    let subject: { chatbot_id?: string; skill_id?: string; subject_tenant_id?: string } | null = null;
    if (pickedAgent) {
      subject = destination === 'agent'
        ? { chatbot_id: pickedAgent.chatbot_id }
        : { skill_id: destination.slice('skill:'.length) };
    } else if (subjectKey) {
      const [kind, id] = subjectKey.split(':');
      subject = kind === 'skill' ? { skill_id: id }
        : kind === 'tenant' ? { subject_tenant_id: id }
          : { chatbot_id: id };
    }
    if (!subject) return;

    setLinking(true);
    try {
      // Always read. Agent file editing is switched off in the product
      // (AGENT_FILE_EDITING_ENABLED), so an edit mode here would promise
      // something that cannot happen — and it kept this screen from writing the
      // same row as the skill and agent editors, which never offered it.
      await vaultService.createAgentLink({
        ...nodeRef,
        ...subject,
        access_mode: 'read',
        include_as_context: asContext,
      });
      message.success(t('vault.share.agentLinked'));
      setSubjectKey(undefined);
      setSubjectQuery('');
      setPickedAgent(null);
      setAsContext(false);
      load(true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.share.agentLinkFailed'));
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (linkId: string) => {
    try {
      await vaultService.deleteAgentLink(linkId);
      message.success(t('vault.share.unlinked'));
      load(true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.share.unlinkFailed'));
    }
  };

  /**
   * Change one person's rights on THIS node.
   *
   * Two different writes behind one gesture, because the person doing it is
   * making one decision: if they already hold a grant here, rewrite it exactly
   * (the only call that can take a right away); if they do not, create one —
   * which for someone reaching this node through their organization is exactly
   * what an override is.
   */
  const handleSaveRights = async (row: VaultAudienceEntry) => {
    setSavingRights(true);
    try {
      if (row.share_id) {
        await vaultService.setShareRights(row.share_id, editRights);
      } else {
        await vaultService.createShare({
          ...nodeRef,
          ...(row.kind === 'tenant'
            ? { shared_with_tenant_id: row.tenant_id }
            : { shared_with_user_id: row.user_id }),
          ...editRights,
          // A rights tweak on an audience row must not, as a side effect,
          // place anything on an organization's shelf.
          ...(row.kind === 'tenant' ? { to_shelf: false } : {}),
        });
      }
      message.success(t('vault.share.rightsSaved'));
      setEditing(null);
      load(true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.share.rightsFailed'));
    } finally {
      setSavingRights(false);
    }
  };

  const startEditing = (row: VaultAudienceEntry) => {
    setEditing(rowKey(row));
    setEditRights({
      can_edit: row.can_edit, can_delete: row.can_delete, can_reshare: row.can_reshare,
    });
  };

  /**
   * One selectable card — used by BOTH tabs, so picking a person and picking an
   * agent are the same gesture. Deliberately shaped like the agents page card:
   * a face, a name and a line that tells two similar entries apart. A dropdown
   * of near-identical strings made that impossible.
   */
  /** The display name behind a picker key, for messages about what failed. */
  const nameForKey = (key: string): string => {
    const [kind, id] = key.split(':');
    if (kind === 'tenant') {
      return targets.tenants.find((x) => x.tenant_id === id)?.tenant_name || id;
    }
    const u = targets.users.find((x) => x.user_id === id);
    return u?.user_full_name || u?.user_email || id;
  };

  const pickerCard = (
    key: string,
    /** Single-select surfaces pass the chosen key; the grantee picker passes
     *  its whole selection, so a card knows it is one of several. */
    selected: string | undefined | string[],
    select: (k: string | undefined) => void,
    avatar: React.ReactNode,
    name: string,
    sub: string | null | undefined,
    /**
     * Why this one cannot be picked. Present = the target already reaches the
     * node, so granting again would write a row that changes nothing. It stays
     * VISIBLE and says why, because a name silently missing from the list reads
     * as a bug — and the sentence is where the person learns the rule.
     */
    blockedBecause?: string,
    /** Non-blocking provenance note ("has access via …") — the card stays
     *  selectable; re-picking sets the grant to the new checkboxes. */
    coveredNote?: string,
  ) => (
    <button
      type="button"
      key={key}
      className={`vault-subject-card${
        (Array.isArray(selected) ? selected.includes(key) : selected === key)
          ? ' selected' : ''}${blockedBecause ? ' is-blocked is-locked' : ''}`}
      aria-pressed={Array.isArray(selected) ? selected.includes(key) : selected === key}
      aria-disabled={!!blockedBecause}
      disabled={!!blockedBecause}
      title={blockedBecause || undefined}
      onClick={() => select(
        Array.isArray(selected)
          ? key                                   // the caller toggles
          : (selected === key ? undefined : key), // single-select unchanged
      )}
    >
      {avatar}
      <span className="vsc-text">
        <span dir="auto" className="vsc-name">{name}</span>
        {/* The reason goes BESIDE what the card already said, never instead of
            it: the second line is how two people called Carol are told apart,
            and losing it to explain a rule would trade one confusion for
            another. */}
        <span dir="auto" className="vsc-desc bidi-clamp">{sub || t('vault.share.noDescription')}</span>
        {blockedBecause && <span className="vsc-blocked">{blockedBecause}</span>}
        {!blockedBecause && coveredNote && (
          <span className="vsc-covered">{coveredNote}</span>
        )}
      </span>
      {blockedBecause ? (
        <span className="vsc-has" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          </svg>
        </span>
      ) : (
        <span className="vsc-tick" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
      )}
    </button>
  );

  const gq = granteeQuery.trim().toLowerCase();
  const shownTenants = targets.tenants.filter(
    (tn) => !gq || tn.tenant_name.toLowerCase().includes(gq),
  );
  const shownUsers = targets.users.filter(
    (u) => !gq || `${u.user_full_name || ''} ${u.user_email} ${u.tenant_name || ''}`.toLowerCase().includes(gq),
  );

  /**
   * Everyone who already reaches this node, by picker key.
   *
   * The managers are in here too: an admin of the owning organization opens
   * everything in it, so "share this folder with them" is a row that grants
   * nothing. Sharing something with a person who already has it is the one
   * gesture this panel should refuse.
   */
  const reaches = new Map<string, VaultAudienceEntry>();
  audience.people.forEach((p) => reaches.set(`user:${p.user_id}`, p));
  audience.managers.forEach((m) => {
    if (!reaches.has(`user:${m.user_id}`)) reaches.set(`user:${m.user_id}`, m);
  });
  audience.organizations.forEach((o) => reaches.set(`tenant:${o.tenant_id}`, o));

  /** Where someone's access comes from, said in one line. */
  const provenance = (row: VaultAudienceEntry): string => {
    const folder = row.source_node?.name || t('vault.share.aFolder');
    const org = row.from_tenant_name || row.name || t('vault.share.aTenant');
    switch (row.via) {
      case 'manager': return t('vault.share.viaManager');
      case 'tenant': return t('vault.share.viaOrg', { org });
      case 'inherited': return t('vault.share.viaFolder', { folder });
      case 'tenant-inherited': return t('vault.share.viaOrgFolder', { org, folder });
      default: return row.email || t('vault.share.viaDirect');
    }
  };

  /** Access that is not this node's to give or take away.
   *
   *  Two kinds, and the card is shown ticked and locked for both:
   *
   *  - a MANAGER of the owning organization already opens everything in it,
   *    so "share this with them" is a row that grants nothing;
   *  - an INHERITED grant lives on a folder ABOVE this one. It reaches here
   *    because grants cascade down, and it can only be changed where it was
   *    made. Offering it as a fresh choice here invited someone to "grant"
   *    what they already had and to believe they could take it back from a
   *    subfolder — which the cascade does not allow (operator decision
   *    2026-08-29). The card says which folder it came from instead.
   *
   *  Everyone else stays selectable: choosing them again SETS their grant to
   *  the new checkboxes (operator decision 2026-08-24). */
  const INHERITED = ['inherited', 'tenant-inherited'];

  const blockedBecause = (key: string): string | undefined => {
    const row = reaches.get(key);
    if (!row) return undefined;
    if (row.via === 'manager') {
      return t('vault.share.alreadyHasAccess', { why: provenance(row) });
    }
    if (INHERITED.includes(row.via as string)) {
      return t('vault.share.fromParentFolder', {
        folder: row.source_node?.name || t('vault.share.aFolder'),
      });
    }
    return undefined;
  };

  /** The non-blocking counterpart: where an already-covered grantee's access
   *  comes from, shown on the card so re-granting is an informed act. This is
   *  for grants made ON THIS NODE, which re-picking legitimately rewrites. */
  const coveredNote = (key: string): string | undefined => {
    const row = reaches.get(key);
    if (!row || row.via === 'manager' || INHERITED.includes(row.via as string)) {
      return undefined;
    }
    return t('vault.share.alreadyHasAccess', { why: provenance(row) });
  };

  /* The SAME three words the grant footer's checkboxes use — Edit, Delete,
     Share on — and not a fourth spelling of each.
     
     The panel used to say "Can reshare" here, the footer "Share on" and the
     folder header "Can share on": three names for one right, in one product,
     within two clicks of each other. That is how somebody grants the sharing
     right and then cannot find it (field report 2026-08-29). It is also why
     the row was too wide to fit on one line. */
  const rightsChips = (row: VaultAudienceEntry) => (
    <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
      <span className="vault-perm-chip">{t('vault.share.view')}</span>
      {row.can_edit && <span className="vault-perm-chip edit">{t('vault.share.permEdit')}</span>}
      {row.can_delete && <span className="vault-perm-chip">{t('vault.share.permDelete')}</span>}
      {row.can_reshare && (
        <span className="vault-perm-chip reshare">{t('vault.share.permReshare')}</span>
      )}
    </Space>
  );

  /**
   * One line of the audience.
   *
   * The rights shown are the EFFECTIVE ones — what this person can actually do
   * — not the contents of a row, because the two differ the moment an
   * organization grant and a personal one meet on the same folder.
   */
  const audienceRow = (row: VaultAudienceEntry) => {
    const key = rowKey(row);
    const open = editing === key;
    const isOrg = row.kind === 'tenant';
    return (
      <div className={`vault-grantee${row.is_override ? ' is-override' : ''}`} key={key}>
        <div className="vault-grantee-line">
          <div className={`vault-avatar ${isOrg ? 'tenant' : ''}`} dir="auto">
            {initials(row.name || '?')}
          </div>
          <div className="vault-grantee-info">
            <Clipped className="gname" title={row.name || ''}>
              {row.name}
              {row.is_override && (
                <span className="vault-tag override">{t('vault.share.overridden')}</span>
              )}
            </Clipped>
            <Clipped
              className="gmeta"
              title={isOrg
                ? t('vault.share.everyonePlural', { n: row.member_count ?? 0 })
                : provenance(row)}
            >
              {isOrg
                // `n`, not `count` — i18next reserves `count` for pluralisation
                // and would go looking for a plural key that does not exist.
                ? t('vault.share.everyonePlural', { n: row.member_count ?? 0 })
                : provenance(row)}
            </Clipped>
          </div>
          {/* The rights and the control that changes them travel TOGETHER on
              the right of the row. They used to be two independent flex items
              with `margin-inline-start: auto` on the action, so as soon as the
              line wrapped the action dropped onto a row of its own and each
              person took three lines of the panel (field report 2026-08-29). */}
          <div className="vault-grantee-actions">
          {rightsChips(row)}
          {canManage && row.via !== 'manager' && (
            row.can_override_here ? (
              <Button size="small" type="text" onClick={() => (open ? setEditing(null) : startEditing(row))}>
                {open ? t('common.cancel') : t('vault.share.change')}
              </Button>
            ) : (
              // Access granted further up is changed further up. An override
              // made here could not reduce it, so offering one would lie.
              <Clipped
                as="span"
                className="vault-managed-elsewhere"
                title={t('vault.share.managedOn',
                         { folder: row.source_node?.name || '' })}
              >
                {t('vault.share.managedOn', { folder: row.source_node?.name || '' })}
              </Clipped>
            )
          )}
          </div>
        </div>

        {open && (
          <div className="vault-rights-editor">
            {/* The SAME icon pills as the grant footer: one control, one
                visual language. This panel kept the old "Can edit" wording
                and default checkboxes, so the drawer taught two different
                vocabularies for the identical three rights. */}
            <div className="vault-perm-row">
              <Checkbox
                checked={editRights.can_edit}
                onChange={(e) => setEditRights({ ...editRights, can_edit: e.target.checked })}
              >
                <Tooltip title={t('vault.share.permEditHint')}><span><EditGlyph /> {t('vault.share.permEdit')}</span></Tooltip>
              </Checkbox>
              <Checkbox
                checked={editRights.can_delete}
                onChange={(e) => setEditRights({ ...editRights, can_delete: e.target.checked })}
              >
                <Tooltip title={t('vault.share.permDeleteHint')}><span><DeleteGlyph /> {t('vault.share.permDelete')}</span></Tooltip>
              </Checkbox>
              <Checkbox
                checked={editRights.can_reshare}
                onChange={(e) => setEditRights({ ...editRights, can_reshare: e.target.checked })}
              >
                <Tooltip title={t('vault.share.permReshareHint')}><span><ReshareGlyph /> {t('vault.share.permReshare')}</span></Tooltip>
              </Checkbox>
            </div>
            {/* Said before they press it, not after: this is a grant for this
                person only, and the organization keeps what it was given. */}
            {!isOrg && row.via.startsWith('tenant') && (
              <div className="vault-rights-hint">
                {t('vault.share.overrideHint', { org: row.from_tenant_name || '' })}
              </div>
            )}
            {!!row.also_from?.length && (
              <div className="vault-rights-hint warn">
                {t('vault.share.alsoFrom', { folders: row.also_from.join(', ') })}
              </div>
            )}
            <div className="vault-rights-actions">
              <Button
                type="primary" size="small" loading={savingRights}
                onClick={() => handleSaveRights(row)}
              >
                {t('common.save')}
              </Button>
              {row.share_id && (
                <Popconfirm
                  title={row.is_override
                    ? t('vault.share.dropOverrideConfirm')
                    : t('vault.share.revokeConfirm')}
                  onConfirm={() => handleRevoke(row.share_id!)}
                >
                  <Button danger size="small" type="text">
                    {row.is_override ? t('vault.share.dropOverride') : t('vault.share.revoke')}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* A single file can be shared with a person or an organization (operator
     decision 2026-08-19, Drive parity). The hint says exactly what the grant
     covers — this file, not the folder around it — and where the grantee
     will find it: their "Shared with me" list. */
  const fileShareNote = !bulk && !node.folder_id && (
    <div className="vault-share-folder-only">{t('vault.share.fileShareHint')}</div>
  );

  /* The audience, one section per KIND. Exactly one of these is on screen at a
     time — the one whose sub-tab is being browsed — and it always shows, with
     a plain "no one has access yet" line when it is empty. An empty section
     that says nothing reads as a broken panel, not an empty state (field
     feedback 2026-08-26). */
  const peopleSection = () => (
    <>
      <div className="vault-share-section-title">
        {t('vault.share.peopleWithAccess')}
        {audience.people.length > 0 && <span className="vault-count">{audience.people.length}</span>}
      </div>
      {audience.people.length === 0 ? (
        <div className="vault-granted-empty">{t('vault.share.noShares')}</div>
      ) : (
        audience.people.map(audienceRow)
      )}
    </>
  );
  const orgsSection = () => (
    <>
      <div className="vault-share-section-title">
        {t('vault.share.orgsWithAccess')}
        {audience.organizations.length > 0 && (
          <span className="vault-count">{audience.organizations.length}</span>
        )}
      </div>
      {audience.organizations.length === 0 ? (
        <div className="vault-granted-empty">{t('vault.share.noOrgsWithAccess')}</div>
      ) : (
        audience.organizations.map(audienceRow)
      )}
    </>
  );

  const peopleTab = (
    <div className="vault-agents-tab">
      {fileShareNote}
      <div className="vault-tab-body">
      {/* WHO ALREADY HAS IT comes first — the state of the world before the
          gesture that changes it. Bounded and scrollable on its own, so a
          long audience never buries the picker (field feedback 2026-08-26).

          It shows the SAME kind the sub-tab is browsing, and only that kind.
          Showing both stacked one panel meant the People tab answered a
          question nobody had asked — "no one has access yet", then an
          organizations list underneath it — which read as a contradiction
          (field feedback 2026-08-29). Nothing is hidden by this: each sub-tab
          carries its own granted/total count, so "Organizations 1/3" says
          plainly that one organization already reaches this. */}
      <div className="vault-granted-box" data-testid="granted-box">
        {bulk ? (
          <>
            <div className="vault-share-section-title">
              {t('vault.bulk.sharingItems')}
              <span className="vault-count">{bulk.items.length}</span>
            </div>
            <ul className="vault-bulk-share-list">
              {bulk.names.slice(0, 12).map((n, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${n}-${i}`}><span dir="auto">{n}</span></li>
              ))}
              {bulk.names.length > 12 && (
                <li className="more">{t('vault.deleteTruth.andMore', { n: bulk.names.length - 12 })}</li>
              )}
            </ul>
            <div className="vault-admins-note">{t('vault.bulk.shareNote')}</div>
          </>
        ) : granteeKind === 'tenant' ? orgsSection() : peopleSection()}
        {audience.managers.length > 0 && (
          <div className="vault-admins-note">{t('vault.share.adminsAlwaysHaveAccess')}</div>
        )}
      </div>
      <div className="vault-tab-scroll">
      {canManage && (
        <>
          <div className="vault-share-section-title">{t('vault.share.addPerson')}</div>

          <div className="vault-subject-switch" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={granteeKind === 'user'}
              className={granteeKind === 'user' ? 'active' : ''}
              /* The two tabs FILTER the list you are browsing; they do not
                 reset the selection. Clearing it here meant picking three
                 people and then adding an organization silently dropped the
                 people — and the grant loop handles both kinds perfectly
                 well, so one pass can address people and organizations
                 together. The chips below keep the whole selection in view. */
              onClick={() => setGranteeKind('user')}
            >
              {t('vault.share.users')}{' '}
              {/* granted / grantable — how much of the possible audience
                  already holds something, at a glance. */}
              <span>{bulk ? targets.users.length : `${audience.people.length}/${targets.users.length}`}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={granteeKind === 'tenant'}
              className={granteeKind === 'tenant' ? 'active' : ''}
              onClick={() => setGranteeKind('tenant')}
            >
              {t('vault.share.tenants')}{' '}
              <span>{bulk ? targets.tenants.length : `${audience.organizations.length}/${targets.tenants.length}`}</span>
            </button>
          </div>

          {/* An organization can receive a folder two ways, and the words
              have to carry the difference — said BEFORE the grant, in plain
              terms, with quiet as the default so picking an organization
              behaves like picking people unless the sharer asks for more.
              Files never go on a shelf, so the choice only shows for
              folders; a tenant file share is always the quiet kind. */}
          {(granteeKind === 'tenant'
            // …and it stays on screen while an organization is selected but
            // the People tab is showing, so the placement being applied is
            // never hidden from the person applying it.
            || granteeKeys.some((k) => k.startsWith('tenant:'))) && anyFolder && (
            <div className="vault-share-org-placement" role="radiogroup"
                 aria-label={t('vault.share.orgPlacement.title')}>
              <label className={`vault-share-org-option${!toShelf ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="org-placement"
                  checked={!toShelf}
                  onChange={() => setToShelf(false)}
                  data-testid="org-placement-quiet"
                />
                <span>
                  <strong>{t('vault.share.orgPlacement.quietLabel')}</strong>
                  <em>{t('vault.share.orgPlacement.quietHint')}</em>
                </span>
              </label>
              <label className={`vault-share-org-option${toShelf ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="org-placement"
                  checked={toShelf}
                  onChange={() => setToShelf(true)}
                  data-testid="org-placement-shelf"
                />
                <span>
                  <strong>{t('vault.share.orgPlacement.shelfLabel')}</strong>
                  <em>{t('vault.share.orgPlacement.shelfHint')}</em>
                </span>
              </label>
            </div>
          )}

          <Input dir="auto"
            allowClear
            value={granteeQuery}
            onChange={(e) => setGranteeQuery(e.target.value)}
            placeholder={granteeKind === 'user' ? t('vault.share.searchUsers') : t('vault.share.searchTenants')}
            style={{ marginBlockEnd: 10 }}
          />

          {/* Same cards as the Agents tab. An organization and a person are told
              apart by the shape of the tile — round for a person, rounded square
              for an organization — the way the grantee rows below already do. */}
          <div className="vault-subject-grid">
            {granteeKind === 'user'
              ? shownUsers.map((u) =>
                  pickerCard(
                    `user:${u.user_id}`,
                    granteeKeys,
                    toggleGrantee,
                    <span className="vsc-initials user" dir="auto">{initials(u.user_full_name || u.user_email)}</span>,
                    u.user_full_name || u.user_email,
                    u.user_full_name
                      ? `${u.user_email}${u.tenant_name ? ` · ${u.tenant_name}` : ''}`
                      : u.tenant_name,
                    blockedBecause(`user:${u.user_id}`),
                    coveredNote(`user:${u.user_id}`),
                  ))
              : shownTenants.map((tn) =>
                  pickerCard(
                    `tenant:${tn.tenant_id}`,
                    granteeKeys,
                    toggleGrantee,
                    <span className="vsc-initials tenant" dir="auto">{initials(tn.tenant_name)}</span>,
                    tn.tenant_name,
                    t('vault.share.everyoneIn', { org: tn.tenant_name }),
                    blockedBecause(`tenant:${tn.tenant_id}`),
                    coveredNote(`tenant:${tn.tenant_id}`),
                  ))}
            {(granteeKind === 'user' ? shownUsers : shownTenants).length === 0 && (
              <div className="vault-empty">
                <p>{gq ? t('vault.share.noMatches') : t('vault.share.noTargets')}</p>
              </div>
            )}
          </div>

        </>
      )}

      </div>
      </div>

      {/* The drawer's floor — the same footer, in the same place, on all three
          tabs. Lists grow and scroll above it; this never moves. */}
      {canManage && (
        <div className="vault-tab-actions">
          {/* Everyone about to be granted, including the ones sitting on the
              tab you are not looking at. This lives on the drawer's fixed
              floor rather than under the picker: inside the scrolling list a
              second row of chips fell below the fold, so a mixed selection —
              the case the chips exist for — was the one you could not see. */}
          {granteeKeys.length > 0 && (
            <div className="vault-share-selected" data-testid="share-selection">
              {granteeKeys.map((k) => (
                <Tag key={k} closable onClose={() => toggleGrantee(k)}
                     className={k.startsWith('tenant:') ? 'is-tenant' : 'is-user'}>
                  {nameForKey(k)}
                </Tag>
              ))}
            </div>
          )}
          <div className="vault-perm-row">
            <Checkbox
              checked={rights.can_edit}
              onChange={(e) => setRights({ ...rights, can_edit: e.target.checked })}
            >
              <Tooltip title={t('vault.share.permEditHint')}><span><EditGlyph /> {t('vault.share.permEdit')}</span></Tooltip>
            </Checkbox>
            <Checkbox
              checked={rights.can_delete}
              onChange={(e) => setRights({ ...rights, can_delete: e.target.checked })}
            >
              <Tooltip title={t('vault.share.permDeleteHint')}><span><DeleteGlyph /> {t('vault.share.permDelete')}</span></Tooltip>
            </Checkbox>
            <Checkbox
              checked={rights.can_reshare}
              onChange={(e) => setRights({ ...rights, can_reshare: e.target.checked })}
            >
              <Tooltip title={t('vault.share.permReshareHint')}><span><ReshareGlyph /> {t('vault.share.permReshare')}</span></Tooltip>
            </Checkbox>
          </div>
          <Button
            type="primary"
            block
            disabled={!granteeKeys.length}
            loading={submitting}
            onClick={handleAddShare}
            data-testid="grant-access"
          >
            {granteeKeys.length > 1
              ? t('vault.share.grantCount', { count: granteeKeys.length })
              : t('vault.share.grant')}
          </Button>
        </div>
      )}
    </div>
  );

  // Already-linked subjects drop out of the picker — of both kinds, since a
  // chatbot and a skill can each hold this node independently.
  // Only links made ON THIS NODE take a subject out of the picker. An
  // inherited row used to count too, which left a covered node with nothing
  // actionable at all: the subject was gone from the picker AND its row was
  // locked — the exact "I can see it is linked but I have nothing to do with
  // it" dead end from the field. A direct link on top of an inherited one is
  // legitimate: it survives the ancestor's unlink.
  const directLinks = usedBy.filter((l) => !l.inherited_from);
  /* The other half: subjects that reach this node through a folder ABOVE it.
     They stay IN the picker — a covered node whose subject had simply vanished
     from the list left people with nothing to look at and no explanation — but
     the card is ticked and locked, and it names the folder the link was made
     on, because that is the only place it can be changed (operator decision
     2026-08-29, extending the same rule from people to agents and skills). */
  const inheritedFrom = new Map<string, string>();
  usedBy.filter((l) => l.inherited_from).forEach((l) => {
    const where = l.inherited_from?.folder_name || '';
    if (l.chatbot_id) inheritedFrom.set(`chatbot:${l.chatbot_id}`, where);
    if (l.skill_id) inheritedFrom.set(`skill:${l.skill_id}`, where);
    if (l.subject_tenant_id) inheritedFrom.set(`tenant:${l.subject_tenant_id}`, where);
  });
  const lockedByParent = (key: string): string | undefined => {
    const where = inheritedFrom.get(key);
    if (where === undefined) return undefined;
    return t('vault.share.fromParentFolder', {
      folder: where || t('vault.share.aFolder'),
    });
  };
  const linkedAgentIds = new Set(directLinks.map((l) => l.chatbot_id).filter(Boolean) as string[]);
  const linkedSkillIds = new Set(directLinks.map((l) => l.skill_id).filter(Boolean) as string[]);

  const q = subjectQuery.trim().toLowerCase();
  const availableBots = bots.filter((b) => !linkedAgentIds.has(b.chatbot_id));
  const availableSkills = skills.filter((s) => !linkedSkillIds.has(s.skill_id));
  const shownBots = availableBots.filter(
    (b) => !q || `${b.chatbot_name} ${b.chatbot_description || ''}`.toLowerCase().includes(q),
  );
  const shownSkills = availableSkills.filter(
    (s) => !q || `${s.skill_name} ${s.skill_description || ''}`.toLowerCase().includes(q),
  );
  const linkedTenantIds = new Set(
    directLinks.map((l) => l.subject_tenant_id).filter(Boolean) as string[],
  );
  const shownOrgSubjects = targets.tenants
    .filter((tn) => !linkedTenantIds.has(tn.tenant_id))
    .filter((tn) => !q || tn.tenant_name.toLowerCase().includes(q));

  const subjectCard = (
    key: string,
    avatar: React.ReactNode,
    name: string,
    description: string | null | undefined,
  ) => pickerCard(
    key,
    subjectKey,
    (k) => {
      setSubjectKey(k);
      // The reading mode belongs to THIS share; a leftover choice from the
      // previous selection must not ride along silently.
      setAsContext(false);
    },
    avatar,
    name,
    description,
    lockedByParent(key),
  );

  /**
   * How the linked content is read — chosen PER SHARE, on the thing being
   * shared, not as one checkbox in the footer. Every subject kind gets the
   * same two honest options: searched when the question calls for it, or
   * pasted into the prompt every turn.
   */
  const contextChoice = (
    <div className="vault-ctx-choice">
      <div className="vault-share-section-title">{t('vault.share.contextHow')}</div>
      <div className="vault-dest-list">
        <button
          type="button"
          className={`vault-dest${!asContext ? ' selected' : ''}`}
          aria-pressed={!asContext}
          onClick={() => setAsContext(false)}
        >
          <span className="vault-dest-title">{t('vault.share.contextSearchable')}</span>
          <span className="vault-dest-sub">{t('vault.share.contextSearchableHint')}</span>
        </button>
        <button
          type="button"
          className={`vault-dest${asContext ? ' selected' : ''}`}
          aria-pressed={asContext}
          onClick={() => setAsContext(true)}
        >
          <span className="vault-dest-title">{t('vault.share.alwaysInContext')}</span>
          <span className="vault-dest-sub">{t('vault.share.alwaysInContextHint')}</span>
        </button>
      </div>
    </div>
  );

  /**
   * Step 2 of the Agents flow. An agent is not one destination: a folder can go
   * to the agent's own knowledge, or to one of the skills it loads — and a skill
   * is shared, so putting it there reaches every agent that loads that skill.
   * The list of skills is the agent's own, which is why this is a second step
   * rather than a flat list of every skill in the tenant.
   */
  const agentDestinationStep = pickedAgent && (
    <div className="vault-dest-step">
      <button type="button" className="vault-back-link" onClick={() => setPickedAgent(null)}>
        <svg className="rtl-flip" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6 6 6" />
        </svg>
        {t('vault.share.backToAgents')}
      </button>

      <div className="vault-subject-card is-static">
        <BotAvatar
          name={pickedAgent.chatbot_name}
          size={38}
          colorOverride={(pickedAgent as any).avatar_color}
          variantOverride={(pickedAgent as any).avatar_variant ?? undefined}
        />
        <span className="vsc-text">
          <span className="vsc-name" dir="auto">{pickedAgent.chatbot_name}</span>
          <span className="vsc-desc bidi-clamp" dir="auto">
            {pickedAgent.chatbot_description || t('vault.share.noDescription')}
          </span>
        </span>
      </div>

      <div className="vault-share-section-title" style={{ marginBlockStart: 16 }}>
        {t('vault.share.whereShouldItGo')}
      </div>

      {agentSkillsLoading ? (
        <div style={{ textAlign: 'center', padding: 18 }}><Spin size="small" /></div>
      ) : (
        <div className="vault-dest-list">
          <button
            type="button"
            className={`vault-dest${destination === 'agent' ? ' selected' : ''}`}
            aria-pressed={destination === 'agent'}
            onClick={() => { setDestination('agent'); setAsContext(false); }}
          >
            <span className="vault-dest-title">{t('vault.share.destAgent')}</span>
            <span className="vault-dest-sub">
              {t('vault.share.destAgentHint', { agent: pickedAgent.chatbot_name })}
            </span>
          </button>

          {agentSkills.map((s) => (
            <button
              type="button"
              key={s.skill_id}
              className={`vault-dest${destination === `skill:${s.skill_id}` ? ' selected' : ''}`}
              aria-pressed={destination === `skill:${s.skill_id}`}
              onClick={() => { setDestination(`skill:${s.skill_id}`); setAsContext(false); }}
            >
              <span className="vault-dest-title">
                <SkillGlyph /> {s.skill_name}
              </span>
              <span className="vault-dest-sub">{t('vault.share.destSkillHint')}</span>
            </button>
          ))}

          {agentSkills.length === 0 && (
            <div className="vault-pick-hint">{t('vault.share.noEditableSkills')}</div>
          )}
        </div>
      )}

      {/* The reading mode for THIS share, chosen right where the destination
          was picked. It is honoured for skills the same way as for agents —
          the earlier "does nothing on a skill" era ended when skill-level
          context was wired through the vault links. */}
      {!agentSkillsLoading && contextChoice}
    </div>
  );

  /**
   * "Used by", limited to the kinds the calling tab is about.
   *
   * Splitting it this way is the point of having three tabs: the Skills tab
   * answers "which skills use this" without a person having to pick that out of
   * a mixed list, and each tab's empty state says something true about that tab
   * rather than about the whole node.
   */
  const usedByGroups = (kinds: readonly VaultAgentLink['subject_type'][], emptyText: string) => {
    const rows = usedBy.filter((l) => kinds.includes(l.subject_type));
    const titles: Record<string, string> = {
      chatbot: t('vault.share.agentsUsing'),
      skill: t('vault.share.skillsUsing'),
      tenant: t('vault.share.tenantsUsing'),
    };
    if (rows.length === 0) {
      // Titled even when empty. An unlabelled sentence floating in a panel
      // reads as content that failed to load; the same sentence under the
      // heading it answers reads as an answer.
      return (
        <>
          <div className="vault-share-section-title">{titles[kinds[0]]}</div>
          <div className="vault-granted-empty">{emptyText}</div>
        </>
      );
    }
    return kinds.map((kind) => {
      const group = rows.filter((l) => l.subject_type === kind);
      if (group.length === 0) return null;
      return (
        <div key={kind}>
          <div className="vault-share-section-title">
            {titles[kind]} <span className="vault-count">{group.length}</span>
          </div>
          {group.map((l) => (
            <div className="vault-grantee" key={l.link_id}>
              {/* The SAME identity line the people rows use. .vault-grantee is
                  display:block (a person's row is a column: identity line plus
                  an optional rights editor), so a row that put its avatar,
                  name and actions directly inside it had nothing laying them
                  out — every agent row stacked into four lines. */}
              <div className="vault-grantee-line">
              {kind === 'chatbot' ? (
                <BotAvatar name={l.subject_name || 'AI'} size={34} />
              ) : (
                <div className={`vault-avatar ${kind === 'skill' ? 'skill' : 'tenant'}`}>
                  {kind === 'skill' ? <SkillGlyph /> : <BotGlyph />}
                </div>
              )}
              <div dir="auto" className="vault-grantee-info">
                <div dir="auto" className="gname">
                  {l.subject_name ||
                    (kind === 'chatbot'
                      ? t('vault.share.agent')
                      : kind === 'skill'
                        ? t('vault.share.skill')
                        : t('vault.share.tenant'))}
                </div>
                <div dir="auto" className="gmeta">
                  {kind === 'chatbot' && t('vault.share.agent')}
                  {kind === 'tenant' && t('vault.share.tenant')}
                  {/* Naming the agents is the point: a skill is a step in the
                      chain, not the end of it. */}
                  {kind === 'skill' && (
                    l.used_by_agents && l.used_by_agents.length > 0
                      ? t('vault.share.skillReaches', { agents: l.used_by_agents.join(', ') })
                      : t('vault.share.skillReachesNobody')
                  )}
                  {/* A link on an ancestor folder reaches this node through
                      the subtree (Drive semantics). It can only be removed
                      where it was made — say where that is. */}
                  {l.inherited_from && (
                    <span className="vault-inherited-note">
                      {' · '}
                      {t('vault.share.linkViaFolder', {
                        folder: l.inherited_from.folder_name,
                      })}
                    </span>
                  )}
                </div>
              </div>
              <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
                <span className={`vault-perm-chip ${l.access_mode === 'edit' ? 'edit' : ''}`}>
                  {/* The same two words the people rows and the grant footer
                      use. `vault.share.edit`/`read` read identically in English
                      today, so this looked harmless — but it is the third
                      spelling of one idea in a panel whose whole point this
                      round was to stop having several. */}
                  {l.access_mode === 'edit'
                    ? t('vault.share.permEdit')
                    : t('vault.share.view')}
                </span>
                {l.include_as_context && (
                  <span className="vault-perm-chip context">{t('vault.share.context')}</span>
                )}
              </Space>
              {l.inherited_from ? (
                /* An inherited link can only be removed where it was made —
                   but a row that merely SAYS so is a dead end: the manager
                   standing on the file has nothing to act on (field report,
                   2026-08-24). The row's link_id IS the ancestor's link, so
                   offer the removal here, with the confirm carrying the blast
                   radius: it takes the link off the whole folder, not just
                   this node. */
                <Popconfirm
                  title={t('vault.share.unlinkParentConfirm', {
                    folder: l.inherited_from.folder_name,
                  })}
                  onConfirm={() => handleUnlink(l.link_id)}
                >
                  <Button danger size="small" type="text"
                          title={t('vault.share.unlinkAtParent', {
                            folder: l.inherited_from.folder_name,
                          })}>
                    {t('vault.share.unlinkAtParentAction', {
                      folder: l.inherited_from.folder_name,
                    })}
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm title={t('vault.share.unlinkConfirm')} onConfirm={() => handleUnlink(l.link_id)}>
                  <Button danger size="small" type="text">
                    {t('vault.share.unlink')}
                  </Button>
                </Popconfirm>
              )}
              </div>
            </div>
          ))}
        </div>
      );
    });
  };

  const agentsTab = canManageAgentLinks ? (
    <div className="vault-agents-tab">
      {/* Everything here scrolls; the grant action below is the last child and
          stays on the drawer's floor whatever the list does. */}
      <div className="vault-tab-body">
      {/* What already uses this node, first — bounded so the picker below
          stays reachable. Hidden while the destination step is open. */}
      {!pickedAgent && (
        <div className="vault-granted-box" data-testid="granted-box">
          {usedByGroups(['chatbot'], t('vault.share.noAgents'))}
        </div>
      )}
      <div className="vault-tab-scroll">
      <div className="vault-share-section-title">{t('vault.share.addAgent')}</div>

      {pickedAgent ? agentDestinationStep : (
      <>
      <Input dir="auto"
        allowClear
        value={subjectQuery}
        onChange={(e) => setSubjectQuery(e.target.value)}
        placeholder={t('vault.share.searchAgents')}
        style={{ marginBlockEnd: 10 }}
      />

      <div className="vault-subject-grid">
        {shownBots.map((b) => {
          const locked = lockedByParent(`chatbot:${b.chatbot_id}`);
          return (
          <button
            type="button"
            key={b.chatbot_id}
            className={`vault-subject-card${locked ? ' is-blocked is-locked selected' : ''}`}
            aria-disabled={!!locked}
            disabled={!!locked}
            title={locked || undefined}
            onClick={() => choosePickedAgent(b)}
          >
            <BotAvatar
              name={b.chatbot_name}
              size={38}
              colorOverride={(b as any).avatar_color}
              variantOverride={(b as any).avatar_variant ?? undefined}
            />
            <span className="vsc-text">
              <span className="vsc-name" dir="auto">{b.chatbot_name}</span>
              <span dir="auto" className="vsc-desc bidi-clamp">{b.chatbot_description || t('vault.share.noDescription')}</span>
              {locked && <span className="vsc-blocked">{locked}</span>}
            </span>
            <span className="vsc-go" aria-hidden="true">
              <svg className="rtl-flip" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </span>
          </button>
          );
        })}
        {shownBots.length === 0 && (
          <div className="vault-empty">
            <p>{q ? t('vault.share.noMatches') : t('vault.share.noneLeft')}</p>
          </div>
        )}
      </div>
      </>
      )}

      </div>
      </div>

      {/* The same footer, in the same place, whichever step this tab is on.
          It arms when there is something to grant; it never moves. */}
      <div className="vault-tab-actions">
        <Button
          type="primary"
          block
          disabled={!pickedAgent || agentSkillsLoading}
          loading={linking}
          onClick={handleAddAgentLink}
        >
          {t('vault.share.grantAgent')}
        </Button>
      </div>
    </div>
  ) : (
    <div className="vault-empty" style={{ padding: '24px 0' }}>
      <p>{t('vault.share.managerOnly')}</p>
    </div>
  );

  /**
   * Skills stand on their own here.
   *
   * A skill was previously reachable only by opening an agent and drilling into
   * the skills it loads, which made a skill look like a property of an agent.
   * It is not: a skill is a subject in its own right, several agents can load
   * the same one, and attaching a folder to the skill once is the whole point.
   * The drill-down stays on the Agents tab as a shortcut — both routes write
   * exactly the same row.
   */
  const skillsTab = canManageAgentLinks ? (
    <div className="vault-agents-tab">
      <div className="vault-tab-body">
      <div className="vault-granted-box" data-testid="granted-box">
        {usedByGroups(['skill'], t('vault.share.noSkills'))}
      </div>
      <div className="vault-tab-scroll">
      <div className="vault-share-section-title">{t('vault.share.addSkill')}</div>

      <Input dir="auto"
        allowClear
        value={subjectQuery}
        onChange={(e) => setSubjectQuery(e.target.value)}
        placeholder={t('vault.share.searchSkills')}
        style={{ marginBlockEnd: 10 }}
      />

      <div className="vault-subject-grid">
        {shownSkills.map((s) =>
          subjectCard(
            `skill:${s.skill_id}`,
            <span className="vsc-skill-tile"><SkillGlyph /></span>,
            s.skill_name,
            s.skill_description,
          ))}
        {shownSkills.length === 0 && (
          <div className="vault-empty">
            <p>{q ? t('vault.share.noMatches') : t('vault.share.noneLeft')}</p>
          </div>
        )}
      </div>

      {/* The reading mode for the share being made, attached to the choice
          rather than hidden in the footer. */}
      {subjectKey?.startsWith('skill:') && contextChoice}

      </div>
      </div>

      {/* The floor of the drawer, always — never wherever the list ends. */}
      <div className="vault-tab-actions">
        <Button
          type="primary"
          block
          disabled={!subjectKey?.startsWith('skill:')}
          loading={linking}
          onClick={handleAddAgentLink}
        >
          {t('vault.share.grantSkill')}
        </Button>
      </div>
    </div>
  ) : (
    <div className="vault-empty" style={{ padding: '24px 0' }}>
      <p>{t('vault.share.managerOnly')}</p>
    </div>
  );

  /**
   * The organization's own context — the fourth subject.
   *
   * A tenant IS a link subject (subject_tenant_id): what its context holds is
   * readable by every agent in that organization, which is exactly what the
   * tenant-context editor's "Add from the Library" writes. This tab is the
   * same grant made from the folder's side, with the same per-share choice.
   */
  const orgTab = canManageAgentLinks ? (
    <div className="vault-agents-tab">
      <div className="vault-tab-body">
      <div className="vault-granted-box" data-testid="granted-box">
        {usedByGroups(['tenant'], t('vault.share.noOrgCtx'))}
      </div>
      <div className="vault-tab-scroll">
      <div className="vault-share-section-title">{t('vault.share.addOrgCtx')}</div>
      <div className="vault-pick-hint" style={{ marginBlockEnd: 10 }}>
        {t('vault.share.orgCtxHint')}
      </div>

      <Input dir="auto"
        allowClear
        value={subjectQuery}
        onChange={(e) => setSubjectQuery(e.target.value)}
        placeholder={t('vault.share.searchOrgs')}
        style={{ marginBlockEnd: 10 }}
      />

      <div className="vault-subject-grid">
        {shownOrgSubjects.map((tn) =>
          subjectCard(
            `tenant:${tn.tenant_id}`,
            <span className="vsc-initials tenant" dir="auto">{initials(tn.tenant_name)}</span>,
            tn.tenant_name,
            (tn as { hierarchy_path?: string }).hierarchy_path || t('vault.share.tenant'),
          ))}
        {shownOrgSubjects.length === 0 && (
          <div className="vault-empty">
            <p>{q ? t('vault.share.noMatches') : t('vault.share.noneLeft')}</p>
          </div>
        )}
      </div>

      {subjectKey?.startsWith('tenant:') && contextChoice}

      </div>
      </div>

      <div className="vault-tab-actions">
        <Button
          type="primary"
          block
          disabled={!subjectKey?.startsWith('tenant:')}
          loading={linking}
          onClick={handleAddAgentLink}
        >
          {t('vault.share.grantOrgCtx')}
        </Button>
      </div>
    </div>
  ) : (
    <div className="vault-empty" style={{ padding: '24px 0' }}>
      <p>{t('vault.share.managerOnly')}</p>
    </div>
  );

  return (
    <Drawer
      placement={endPlacement(direction)}
      open
      onClose={onClose}
      /* Wide enough for TWO columns: who already has it beside who you can
         give it to, so a person sees both lists at once instead of
         scrolling between them (operator, 2026-08-29). Capped against the
         viewport so a narrow window still gets the single-column layout the
         CSS falls back to. */
      width="min(920px, 96vw)"
      rootClassName="vault-share-drawer"
      title={bulk
        ? t('vault.bulk.shareTitle', { n: bulk.items.length })
        : `${t('vault.share.title')} — ${node.name}`}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            // Each tab owns its own picker state; carrying a half-made
            // selection across would arm the wrong grant button.
            setActiveTab(key);
            setSubjectKey(undefined);
            setSubjectQuery('');
            setPickedAgent(null);
            setAsContext(false);
          }}
          items={[
            // Who may attach folders to agents/skills/context is a ROLE
            // question — see canManageAgentLinks above. People sits LAST
            // (operator decision 2026-08-26): the permissions tab is the
            // drawer's closing word, and the one every admin role sees.
            ...(canManageAgentLinks
              ? [
                  { key: 'agents', label: t('vault.share.agents'), children: agentsTab },
                  { key: 'skills', label: t('vault.share.skills'), children: skillsTab },
                  { key: 'org', label: t('vault.share.orgTab'), children: orgTab },
                ]
              : []),
            { key: 'people', label: t('vault.share.people'), children: peopleTab },
          ]}
        />
      )}
    </Drawer>
  );
}
